// src/components/popups/ChoosePaymentMethodDrawer.js
import React, { useEffect, useMemo, useState } from 'react';
import './ChoosePaymentMethodDrawer.css';

export default function ChoosePaymentMethodDrawer({
  open,
  onClose,
  isMainnet,
  amountPi = 20,
  defaultCode = '30dayfree',
  busy = false,

  // Actions from parent
  onPayWithPi,
  onRedeemCode,
}) {
  const [method, setMethod] = useState('pi'); // 'pi' | 'code'
  const [code, setCode] = useState(defaultCode);
  const [localErr, setLocalErr] = useState('');

  const canConfirm = useMemo(() => {
    if (!isMainnet) return false;
    if (busy) return false;
    if (method === 'pi') return true;
    return !!code.trim();
  }, [isMainnet, busy, method, code]);

  useEffect(() => {
    if (!open) return;
    setLocalErr('');
    setMethod('pi');
    setCode(defaultCode);
  }, [open, defaultCode]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (open) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const confirm = async () => {
    setLocalErr('');

    if (!isMainnet) {
      setLocalErr('Mainnet only. Please open barterfi.app to proceed.');
      return;
    }

    try {
      if (method === 'pi') {
        await onPayWithPi?.();
      } else {
        if (!code.trim()) {
          setLocalErr('Enter a code to continue.');
          return;
        }
        await onRedeemCode?.(code.trim());
      }
    } catch (e) {
      // parent already shows the main error banner,
      // but keep a fallback for unexpected cases
      setLocalErr(e?.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <div className="pmethod-overlay" onClick={onClose} />
      <div className="pmethod-sheet" role="dialog" aria-modal="true" aria-label="Choose payment method">
        <div className="pmethod-handle" />

        <div className="pmethod-head">
          <h3>Choose payment method</h3>
          <button className="pmethod-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!isMainnet && (
          <div className="pmethod-warning">
            You’re on <strong>Testnet</strong>. Payments require <strong>Mainnet</strong> (barterfi.app).
          </div>
        )}

        {localErr && <div className="pmethod-error" role="alert">{localErr}</div>}

        <div className="pmethod-options">
          <button
            className={`pmethod-option ${method === 'pi' ? 'active' : ''}`}
            onClick={() => setMethod('pi')}
            disabled={busy}
          >
            <div className="pmethod-option-title">Pay with Pi</div>
            <div className="pmethod-option-sub">{amountPi} Pi (30 days)</div>
          </button>

          <button
            className={`pmethod-option ${method === 'code' ? 'active' : ''}`}
            onClick={() => setMethod('code')}
            disabled={busy}
          >
            <div className="pmethod-option-title">Use code</div>
            <div className="pmethod-option-sub">Redeem a trial / promo code</div>
          </button>
        </div>

        {method === 'code' && (
          <div className="pmethod-code">
            <label className="pmethod-label" htmlFor="pmethod-code-input">Code</label>
            <input
              id="pmethod-code-input"
              className="pmethod-input"
              placeholder="Enter code (e.g., 30dayfree)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={busy}
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <div className="pmethod-hint">
              One-time free 30-day trial for eligible accounts.
            </div>
          </div>
        )}

        <div className="pmethod-actions">
          <button className="pmethod-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="pmethod-primary"
            onClick={confirm}
            disabled={!canConfirm}
            title={!isMainnet ? 'Open barterfi.app (Mainnet) to continue' : ''}
          >
            {busy ? 'Processing…' : (method === 'pi' ? `Confirm & Pay (${amountPi} Pi)` : 'Apply Code')}
          </button>
        </div>
      </div>
    </>
  );
}
